import { Close, RefreshOutlined } from '@mui/icons-material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  ButtonBase,
  Divider,
  ListItemText,
  Menu,
  Typography,
} from '@mui/material';
import { usePageManager } from 'hooks/pageManager/usePageManager';
import { useAtom } from 'jotai';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isSidebarOpenAtom } from 'store/private/layout/layoutAtoms';

import {
  NavMenuItem,
  tabCelWidth,
  TabItem,
  TabsContainer,
} from './Tabs.styled';

function isOverflow(element) {
  if (!element) return;
  return element.scrollWidth > element.clientWidth;
}

function genListMenuAndTabs(element, items, pages = []) {
  const d = { tab: [], menu: Array.from(pages) };
  if (!element) return d;
  if (
    !isOverflow(element) &&
    element.clientWidth > pages.length * (tabCelWidth + 10)
  )
    return { tab: Array.from(pages), menu: [] };
  let w = element.clientWidth;
  // eslint-disable-next-line no-unmodified-loop-condition,fp/no-loops
  while (w >= tabCelWidth + 8) {
    if (d.menu[0]) {
      w -= tabCelWidth + 8;
      d.tab.push(d.menu.shift());
    } else {
      return d;
    }
  }
  return d;
}

export const DesktopTabs = () => {
  const {
    pages,
    setPages,
    activeTabId,
    setActiveTabId,
    closeTabById,
    skipNextNavigation,
    refreshTabById,
  } = usePageManager();

  const navigate = useNavigate();
  const [anchor, setAnchor] = useState(null);
  const [items, setItems] = useState({ menu: [], tab: [] });
  const tabRef = useRef();
  const [isSidebarOpen] = useAtom(isSidebarOpenAtom);

  const closeTab = tab => e => {
    e.stopPropagation();
    closeTabById(tab.id);
    if (items.menu.length === 1) {
      setAnchor(null);
    }
  };
  const calc = () => {
    const list = genListMenuAndTabs(tabRef.current, items, Array.from(pages));
    setItems(list);
  };

  const isResizeListener = useRef(false);

  const promoteTabToFront = tab => {
    if (!tab || pages[0]?.id === tab.id) return;
    const reordered = [tab, ...pages.filter(p => p.id !== tab.id)];
    setPages(reordered);
    setItems(genListMenuAndTabs(tabRef.current, items, reordered));
  };

  const onMenuTabClick = tab => {
    promoteTabToFront(tab);
    skipNextNavigation.current = true;
    setActiveTabId(tab.id);
    navigate(tab.href);
    setAnchor(null);
  };

  useEffect(() => {
    if (!isResizeListener.current) {
      calc();
      window.addEventListener('resize', calc);
      isResizeListener.current = true;
    } else {
      setTimeout(() => calc(), 700);
    }
    return () => {
      try {
        if (isResizeListener.current) {
          window.removeEventListener('resize', calc);
          isResizeListener.current = false;
        }
      } catch (e) {
        console.log(e);
      }
    };
  }, [pages]);

  useEffect(() => {
    if (items.tab.length || items.menu.length) {
      setTimeout(() => calc(), 240);
    }
  }, [isSidebarOpen]);

  // Sidebar (or any activator): if the active tab is in the overflow menu,
  // pull it out to the front of the visible tab list. Visible tabs stay put.
  useEffect(() => {
    const menuTab = items.menu.find(t => t.id === activeTabId);
    if (!menuTab) return;
    promoteTabToFront(menuTab);
  }, [activeTabId, items.menu]);

  return (
    <Box
      sx={{
        'direction': 'rtl',
        '*': {
          direction: 'rtl',
        },
        'display': 'flex',
        'alignItems': 'center',
        'gap': '5px',
      }}
    >
      <TabsContainer ref={tabRef}>
        {items.tab?.map(tab => (
          <Fragment key={tab.id}>
            <TabItem
              onClick={() => {
                skipNextNavigation.current = true;
                setActiveTabId(tab.id);
                navigate(tab.href);
              }}
              selected={tab.id === activeTabId}
            >
              <Box>
                <Typography
                  fontSize="inherit"
                  whiteSpace="no-wrap"
                  width="130px"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  title={tab.label}
                  sx={{
                    ml: '8px',
                  }}
                >
                  {tab.label}
                </Typography>
                {tab.id === activeTabId && (
                  <RefreshOutlined
                    className="refresh-icon"
                    fontSize="small"
                    onClick={e => {
                      e.stopPropagation();
                      refreshTabById(tab.id);
                    }}
                  />
                )}
                <Close
                  className="close-icon"
                  fontSize="small"
                  onClick={closeTab(tab)}
                />
              </Box>
            </TabItem>
            <Divider orientation="vertical" flexItem />
          </Fragment>
        ))}
      </TabsContainer>

      {items?.menu?.length ? (
        <ButtonBase
          sx={{
            'bgcolor': 'background.paper',
            'p': '4px',
            'borderRadius': '4px',
            '&:disabled': {
              opacity: '.7',
            },
          }}
          disabled={!items?.menu?.length}
          onClick={e => setAnchor(e.currentTarget)}
        >
          <KeyboardArrowDownIcon fontSize="small" />
        </ButtonBase>
      ) : null}

      <Menu
        open={!!anchor}
        onClose={() => setAnchor(null)}
        anchorEl={anchor}
        PaperProps={{
          sx: {
            width: '208px',
            boxShadow:
              '0 4px 20px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.08)',
            bgcolor: 'background.paper',
            paddingInline: '8px',
          },
        }}
      >
        {items?.menu?.map(tab => (
          <NavMenuItem
            key={tab.id}
            selected={tab.id === activeTabId}
            onClick={() => onMenuTabClick(tab)}
          >
            <ListItemText
              primary={tab.label}
              sx={{
                'marginInline': '2px',
                '*': {
                  fontSize: '12px!important',
                  whiteSpace: 'nowrap',
                },
                'ml': 'auto',
                'textAlign': 'right',
              }}
            />
            {tab.id === activeTabId && (
              <RefreshOutlined
                className="refresh-icon"
                fontSize="small"
                onClick={e => {
                  e.stopPropagation();
                  refreshTabById(tab.id);
                }}
              />
            )}
            <Close
              className="close-icon"
              fontSize="small"
              onClick={closeTab(tab)}
            />
          </NavMenuItem>
        ))}
        {!items?.menu?.length && <Typography color="white">خالی</Typography>}
      </Menu>
    </Box>
  );
};
